import { MessagePrimitive, type ToolCallMessagePart, useAui, useAuiState } from '@assistant-ui/react';
import { AnimatePresence } from 'motion/react';
import { useDeferredValue } from 'react';
import { useShallow } from 'zustand/shallow';

import { CollapsibleToolRun } from './CollapsibleToolRun.tsx';
import classes from './Thread.module.css';
import { AssistantBubble } from '#plugin-chat/components/bubble/AssistantBubble.tsx';
import { ProgressBubble } from '#plugin-chat/components/bubble/ProgressBubble.tsx';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { isErroredToolPart } from '#plugin-chat/hooks/isErroredToolPart.ts';
import { useHasPendingToolCall } from '#plugin-chat/hooks/useHasPendingToolCall.ts';
import { useToolsMap } from '#plugin-chat/hooks/useToolsMap.ts';

type GroupablePart = {
  type: string;
  toolName?: string;
  isError?: boolean;
  status?: { type?: string; reason?: string };
  // Carried so `isErroredToolPart` can classify a `<Tool>`-caught failure
  // (resolved `{ success: false }` envelope). assistant-ui `ThreadMessage`
  // tool-call parts expose the resolved value as `result` (cf. `useToolPart`,
  // `tool-identity`), NOT the model-message `output` field; `state.message.
  // parts` already supplies the full part objects, so this only widens the
  // type.
  result?: unknown;
};

/**
 * Segment of message parts to render as a single unit.
 *
 * - `passthrough`: a non-collapsible part (text, reasoning, non-collapsible
 *   tool, etc.) rendered via `MessagePrimitive.PartByIndex`.
 * - `collapsible-run`: one or more consecutive collapsible tool parts rendered
 *   together inside a `CollapsibleToolRun`.
 */
type PartSegment = { kind: 'passthrough'; index: number } | { kind: 'collapsible-run'; indices: number[] };

/**
 * Walk parts left-to-right and group consecutive collapsible parts into runs;
 * every other part becomes its own `passthrough` segment. Pure reducer — the
 * caller supplies the closure that decides collapsibility for the current
 * message context.
 */
function groupParts<PartType>(parts: readonly PartType[], isCollapsible: (part: PartType) => boolean): PartSegment[] {
  return parts.reduce<PartSegment[]>((segments, part, index) => {
    if (!isCollapsible(part)) {
      segments.push({ kind: 'passthrough', index });
      return segments;
    }
    const last = segments.at(-1);
    if (last?.kind === 'collapsible-run') {
      last.indices.push(index);
    } else {
      segments.push({ kind: 'collapsible-run', indices: [index] });
    }
    return segments;
  }, []);
}

/**
 * Walk message parts left-to-right and group consecutive collapsible tool-call
 * parts into runs. Every other part becomes its own `passthrough` segment.
 *
 * Collapsibility is derived from each tool's `collapsible` sidecar flag on
 * its model-context entry (read via {@link useToolsMap}), OR from the part
 * being in a terminal failure state (see {@link isErroredToolPart}). Errored
 * tool calls are folded into the run accordion regardless of their declared
 * `collapsible` — once a tool has failed, its inline output is no longer
 * useful and visually belongs with the other tool-call rows.
 *
 * Takes `parts` as a parameter rather than subscribing internally so the
 * caller can share a single `useAuiState((s) => s.message.parts)` subscription
 * with the rest of the render — the previous shape ran the selector twice
 * per render (once here, once at the call site), which fired on every chunk
 * during streaming.
 */
function useGroupedParts(): PartSegment[] {
  const parts = useAuiState(useShallow((state) => state.message.parts));
  // Read the tools map once at the top level; the closure below does plain
  // object access, so it's safe to call inside groupParts' per-part loop.
  const tools = useToolsMap();
  const isCollapsible = (part: GroupablePart): boolean => {
    if (part.type !== 'tool-call') {
      return false;
    }
    const isCollapsibleTool = part.toolName !== undefined && tools?.[part.toolName]?.collapsible === true;
    return isCollapsibleTool || isErroredToolPart(part);
  };

  return groupParts(parts, isCollapsible);
}

/**
 * Top-level message-parts renderer for assistant messages.
 *
 * Splits parts into segments — consecutive collapsible tool calls become
 * one `CollapsibleToolRun`; every other part passes through unchanged via
 * `MessagePrimitive.PartByIndex` with the standard component overrides
 * (`Text: AssistantBubble`, `tools.Fallback: ToolDisplay`).
 */
export function CollapsibleMessageParts() {
  const isRunning = useAuiState((state) => state.message.status?.type === 'running');
  const aui = useAui();
  const segments = useGroupedParts();
  const hasPendingToolCall = useHasPendingToolCall();
  const parts = aui.message().getState().parts;

  const progressToolPart = useDeferredValue(
    segments.reduce(
      (acc, segment, segmentIndex) => {
        if (segment.kind === 'collapsible-run') {
          const lastIndex = segment.indices.at(-1);
          const lastPart = lastIndex === undefined ? undefined : parts.at(lastIndex);
          const toolPart = lastPart?.type === 'tool-call' ? lastPart : undefined;
          const isLastPart = segment.indices.some((i) => i >= parts.length - 1);
          if (isRunning && isLastPart) {
            return toolPart ?? null;
          }
        }

        const lastPart = parts.at(-1);
        const toolPart = lastPart?.type === 'tool-call' ? lastPart : undefined;
        if (isRunning && !hasPendingToolCall && segmentIndex === segments.length - 1) {
          return toolPart ?? null;
        }

        return acc;
      },
      undefined as ToolCallMessagePart | undefined | null,
    ),
  );

  return (
    <AnimatePresence>
      <div className={classes.messageParts}>
        {segments.map((segment, segmentIndex) => {
          if (segment.kind === 'collapsible-run') {
            return (
              <CollapsibleToolRun
                indices={segment.indices}
                // biome-ignore lint/suspicious/noArrayIndexKey: Segment order is stable within a message render.
                key={`run-${segmentIndex}`}
              />
            );
          }

          return (
            <MessagePrimitive.PartByIndex components={components} index={segment.index} key={`part-${segment.index}`} />
          );
        })}
      </div>
      {((!segments.length && isRunning) || !!progressToolPart || progressToolPart === null) && (
        <ProgressBubble key="progress" toolPart={progressToolPart ?? undefined} />
      )}
    </AnimatePresence>
  );
}

const components = {
  Text: AssistantBubble,
  tools: { Fallback: ToolDisplay },
};
